import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sessionActions } from '../../store';

// Watches the phone's own position while enabled and publishes it to the
// store so the map can draw it. Nothing leaves the device: the distance to
// the vehicle is computed here, not on the server.
//
// Failure is deliberately quiet. On a wrapper app that has not been granted
// location, or on a phone with no fix, the marker and distance simply do not
// appear; the Live session itself carries on.
export default (enabled) => {
  const dispatch = useDispatch();
  const location = useSelector((state) => state.session.agentLocation);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !navigator.geolocation) {
      return undefined;
    }
    setError(null);
    const watch = navigator.geolocation.watchPosition(
      ({ coords, timestamp }) => {
        setError(null);
        dispatch(sessionActions.updateAgentLocation({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          time: timestamp,
        }));
      },
      (failure) => setError(failure),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    return () => {
      navigator.geolocation.clearWatch(watch);
      dispatch(sessionActions.updateAgentLocation(null));
    };
  }, [enabled]);

  return { location, error };
};

const EARTH_RADIUS_M = 6371008.8;

export const distanceMeters = (from, to) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(to.latitude - from.latitude);
  const dLon = toRad(to.longitude - from.longitude);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(from.latitude)) * Math.cos(toRad(to.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
};

// Short form for "how far away": feet or metres when close, one decimal in
// the large unit otherwise. The stock formatter rounds to whole miles, which
// is useless at recovery range.
export const formatNearDistance = (meters, unit) => {
  if (unit === 'mi') {
    const miles = meters / 1609.344;
    return miles < 0.5 ? `${Math.round(meters * 3.28084)} ft` : `${miles.toFixed(1)} mi`;
  }
  if (unit === 'nmi') {
    return `${(meters / 1852).toFixed(2)} nmi`;
  }
  return meters < 500 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
};
